import { matchPath } from 'react-router-dom'
import { RoutesData } from '@/router/RoutesData'
import { ASSISTANT_SUGGESTED_PROMPTS } from './copy'
import type { AssistantNudge } from './nudge'

/** A starter question; one about a task also names its case (as context). */
export type AssistantPrompt = string | { text: string; caseId: string }

export const promptText = (prompt: AssistantPrompt) =>
  typeof prompt === 'string' ? prompt : prompt.text

export interface AssistantPageSuggestions {
  /** Shown as "Suggested for <label>". */
  label: string
  /** Up to three starter questions, written the way an attorney asks. */
  prompts: AssistantPrompt[]
}

const MY_CASES_SECTIONS: Record<string, string> = {
  'my-tasks': 'My Tasks',
  inbound: 'Inbound Cases',
  outbound: 'Outbound Cases',
  signed: 'Signed Cases',
}

const ATTENTION = 'What needs my attention?'

export const DEFAULT_ASSISTANT_SUGGESTIONS: AssistantPageSuggestions = {
  label: 'you',
  prompts: [...ASSISTANT_SUGGESTED_PROMPTS],
}

const matches = (pattern: string, pathname: string) =>
  matchPath(pattern, pathname)

/**
 * Picks the empty state's label and starter questions from the page the
 * user is on. When an urgent task is known, the first My Tasks follow-up
 * asks about it by name.
 */
export const getAssistantPageSuggestions = (
  pathname: string,
  topTask?: Pick<AssistantNudge, 'question' | 'caseId'> | null
): AssistantPageSuggestions => {
  const myCases = matches(RoutesData.RMyReferrals.path, pathname)
  if (myCases) {
    const section = myCases.params.id ?? ''
    const label = MY_CASES_SECTIONS[section] ?? 'My Cases'
    if (section === 'my-tasks') {
      return {
        label,
        prompts: [
          ATTENTION,
          topTask
            ? { text: topTask.question, caseId: topTask.caseId }
            : 'Which invitations expire soon?',
          'Which cases are waiting on me?',
        ],
      }
    }
    if (section === 'outbound') {
      return {
        label,
        prompts: [
          ATTENTION,
          'Which of my referrals have new proposals?',
          'Help me post a referral',
        ],
      }
    }
    return {
      label,
      prompts: [
        ATTENTION,
        'Which cases are waiting on me?',
        'Which cases need a status update?',
      ],
    }
  }

  if (matches(RoutesData.RMarketplace.path, pathname)) {
    return {
      label: 'Marketplace',
      prompts: [
        'Find cases that fit my practice areas',
        'Show the newest cases in my state',
        'What fee is typical for these cases?',
      ],
    }
  }

  if (matches(RoutesData.RCase.path, pathname)) {
    return {
      label: 'this case',
      prompts: [
        'Summarize this case',
        'What’s the next step on this case?',
        'Who has made proposals on this case?',
      ],
    }
  }

  if (matches(RoutesData.RCreateNewCase.path, pathname)) {
    return {
      label: 'Create Case',
      prompts: [
        'Help me post a referral',
        'Turn my intake notes into a referral',
        'What makes a referral get picked up quickly?',
      ],
    }
  }

  return DEFAULT_ASSISTANT_SUGGESTIONS
}
